// Reactive core — browser rendering. Clone-and-bind for fresh renders, and true
// hydration that adopts existing Declarative Shadow DOM instead of discarding it.
// Fine-grained: each dynamic binding updates only its own node(s) via an effect.

import { effect, isSignal, read } from "../core/signal";
import {
  classifyHoles,
  templateHtml,
  isTemplateResult,
  isRepeatResult,
  isUnsafeHtml,
  type TemplateResult,
  type RepeatResult,
  type PartMeta,
} from "../core/html";

export interface Scope {
  add(disposer: () => void): void;
  dispose(): void;
}

export function makeScope(): Scope {
  const disposers: Array<() => void> = [];
  return {
    add: (d) => disposers.push(d),
    dispose() {
      for (const d of disposers.splice(0)) d();
    },
  };
}

interface CompiledTemplate {
  template: HTMLTemplateElement;
  parts: Array<PartMeta & { path: number[] }>;
}

const cache = new WeakMap<TemplateStringsArray, CompiledTemplate>();

function compile(strings: TemplateStringsArray): CompiledTemplate {
  const cached = cache.get(strings);
  if (cached) return cached;
  const metas = classifyHoles(strings as unknown as string[]);
  const template = document.createElement("template");
  template.innerHTML = templateHtml(strings as unknown as string[], metas);
  const paths = new Map<number, number[]>();
  walk(template.content, [], paths);
  const parts = metas.map((m) => ({ ...m, path: paths.get(m.index) ?? [] }));
  const compiled: CompiledTemplate = { template, parts };
  cache.set(strings, compiled);
  return compiled;
}

function walk(node: Node, path: number[], out: Map<number, number[]>): void {
  const children = node.childNodes;
  for (let i = 0; i < children.length; i++) {
    const child = children[i]!;
    const childPath = [...path, i];
    if (child.nodeType === Node.COMMENT_NODE) {
      const m = /^hc:(\d+)$/.exec((child as Comment).data);
      if (m) out.set(Number(m[1]), childPath);
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      const el = child as Element;
      for (const attr of [...el.attributes]) {
        const m = /^data-hcb-(\d+)$/.exec(attr.name);
        if (m) out.set(Number(m[1]), childPath);
      }
      walk(el, childPath, out);
    }
  }
}

function resolvePath(root: Node, path: number[]): Node | undefined {
  let n: Node | undefined = root;
  for (const idx of path) {
    if (!n) return undefined;
    n = n.childNodes[idx];
  }
  return n;
}

function findEnd(start: Comment, index: number): Comment {
  let n: Node | null = start.nextSibling;
  const tag = `/hc:${index}`;
  while (n && !(n.nodeType === Node.COMMENT_NODE && (n as Comment).data === tag)) {
    n = n.nextSibling;
  }
  return n as Comment;
}

function rangeText(start: Comment, end: Comment): string {
  let t = "";
  let n = start.nextSibling;
  while (n && n !== end) {
    t += n.textContent ?? "";
    n = n.nextSibling;
  }
  return t;
}

function clearRange(start: Comment, end: Comment): void {
  let n = start.nextSibling;
  while (n && n !== end) {
    const next = n.nextSibling;
    n.parentNode?.removeChild(n);
    n = next;
  }
}

function isPrimitive(v: unknown): v is string | number {
  return typeof v === "string" || typeof v === "number";
}

interface RepeatEntry {
  scope: Scope;
  nodes: Node[];
}

class NodePart {
  private kind: "empty" | "text" | "nodes" | "repeat" = "empty";
  private textNode: Text | null = null;
  private childScope: Scope = makeScope();
  private keyed = new Map<unknown, RepeatEntry>();
  private committedText: string | null = null;

  constructor(
    private readonly start: Comment,
    private readonly end: Comment,
  ) {}

  set(value: unknown, hydratingEqual: boolean): void {
    if (value == null || value === false || value === true) {
      if (hydratingEqual && rangeText(this.start, this.end).trim() === "") return;
      this.teardown();
      this.kind = "empty";
      return;
    }
    if (isPrimitive(value)) {
      const text = String(value);
      if (hydratingEqual && this.kind === "empty" && rangeText(this.start, this.end) === text) {
        // Adopt the server-rendered text node so later updates mutate it in place.
        const adopted = this.start.nextSibling;
        if (adopted && adopted.nodeType === Node.TEXT_NODE && adopted.nextSibling === this.end) {
          this.textNode = adopted as Text;
        }
        this.committedText = text;
        this.kind = "text";
        return;
      }
      if (this.kind === "text" && this.committedText === text) return;
      if (this.kind !== "text") this.teardown();
      this.setText(text);
      return;
    }
    if (isUnsafeHtml(value)) {
      this.teardown();
      const tpl = document.createElement("template");
      tpl.innerHTML = value.value;
      this.insertNodes([...tpl.content.childNodes]);
      this.kind = "nodes";
      return;
    }
    if (isRepeatResult(value)) {
      this.reconcile(value);
      return;
    }
    if (isTemplateResult(value)) {
      this.teardown();
      const nodes = this.instantiateInto(value, this.childScope);
      this.insertNodes(nodes);
      this.kind = "nodes";
      return;
    }
    if (Array.isArray(value)) {
      this.teardown();
      const all: Node[] = [];
      for (const item of value) {
        if (isTemplateResult(item)) all.push(...this.instantiateInto(item, this.childScope));
        else all.push(document.createTextNode(String(read(item) ?? "")));
      }
      this.insertNodes(all);
      this.kind = "nodes";
      return;
    }
    // Fallback: coerce to text.
    this.teardown();
    this.setText(String(value));
  }

  private setText(text: string): void {
    if (!this.textNode) {
      clearRange(this.start, this.end); // drop any untracked server nodes
      this.textNode = document.createTextNode(text);
      this.end.parentNode?.insertBefore(this.textNode, this.end);
    } else {
      this.textNode.data = text;
    }
    this.committedText = text;
    this.kind = "text";
  }

  private insertNodes(nodes: Node[]): void {
    for (const n of nodes) this.end.parentNode?.insertBefore(n, this.end);
  }

  private instantiateInto(result: TemplateResult, scope: Scope): Node[] {
    const compiled = compile(result.strings);
    const frag = compiled.template.content.cloneNode(true) as DocumentFragment;
    bindCompiled(frag, compiled, scope, result.values, false);
    return [...frag.childNodes];
  }

  private reconcile(r: RepeatResult): void {
    if (this.kind !== "repeat") {
      this.teardown();
      this.kind = "repeat";
      this.keyed = new Map();
    }
    const oldMap = this.keyed;
    const newMap = new Map<unknown, RepeatEntry>();
    const newKeys = new Set(r.items.map((it, i) => r.key(it, i)));
    for (const [k, entry] of oldMap) {
      if (!newKeys.has(k)) {
        entry.scope.dispose();
        for (const n of entry.nodes) n.parentNode?.removeChild(n);
      }
    }
    let cursor: Node | null = this.start.nextSibling;
    for (let i = 0; i < r.items.length; i++) {
      const item = r.items[i] as unknown;
      const k = r.key(item, i);
      let entry = oldMap.get(k);
      if (!entry) {
        const scope = makeScope();
        const nodes = this.instantiateInto(r.template(item, i), scope);
        entry = { scope, nodes };
        for (const n of nodes) this.end.parentNode?.insertBefore(n, cursor);
      } else {
        for (const node of entry.nodes) {
          if (node === cursor) cursor = cursor.nextSibling;
          else this.end.parentNode?.insertBefore(node, cursor);
        }
      }
      newMap.set(k, entry);
      const last = entry.nodes[entry.nodes.length - 1];
      cursor = last ? last.nextSibling : cursor;
    }
    this.keyed = newMap;
  }

  private teardown(): void {
    this.childScope.dispose();
    this.childScope = makeScope();
    for (const entry of this.keyed.values()) entry.scope.dispose();
    this.keyed.clear();
    clearRange(this.start, this.end);
    this.textNode = null;
    this.committedText = null;
  }
}

/**
 * Resolve every part's target node FIRST (holding references), then bind. If we
 * resolved lazily during binding, an earlier node binding that inserts content
 * would shift later siblings' indices and invalidate their precomputed paths.
 */
function bindCompiled(
  root: Node,
  compiled: CompiledTemplate,
  scope: Scope,
  values: readonly unknown[],
  hydrating: boolean,
): void {
  const targets = compiled.parts.map((p) => resolvePath(root, p.path));
  compiled.parts.forEach((part, i) => {
    const node = targets[i];
    if (node) bindPart(node, part, scope, values[part.index], hydrating);
  });
}

function bindPart(
  node: Node,
  part: PartMeta & { path: number[] },
  scope: Scope,
  value: unknown,
  hydrating: boolean,
): void {
  if (part.type === "node") {
    const start = node as Comment;
    const end = findEnd(start, part.index);
    if (!end) return; // marker pair not found (defensive)
    const nodePart = new NodePart(start, end);
    let first = true;
    const apply = (v: unknown): void => {
      const hydratingEqual = first && hydrating;
      first = false;
      nodePart.set(v, hydratingEqual);
    };
    bindReactive(scope, value, apply);
    return;
  }

  const el = node as Element;
  el.removeAttribute(`data-hcb-${part.index}`);

  if (part.type === "event") {
    if (typeof value === "function") {
      el.addEventListener(part.name, value as EventListener);
      scope.add(() => el.removeEventListener(part.name, value as EventListener));
    }
    return;
  }

  let first = true;
  const apply = (v: unknown): void => {
    const skipInitial = first && hydrating;
    first = false;
    if (part.type === "attr") {
      const next = v == null || v === false ? null : String(v);
      if (skipInitial && el.getAttribute(part.name) === (next ?? null)) return;
      if (next == null) el.removeAttribute(part.name);
      else el.setAttribute(part.name, next);
    } else if (part.type === "bool") {
      if (skipInitial && el.hasAttribute(part.name) === Boolean(v)) return;
      el.toggleAttribute(part.name, Boolean(v));
    } else {
      // prop
      (el as unknown as Record<string, unknown>)[part.name] = v;
    }
  };
  bindReactive(scope, value, apply);
}

function bindReactive(scope: Scope, value: unknown, apply: (v: unknown) => void): void {
  if (isSignal(value) || typeof value === "function") {
    scope.add(effect(() => apply(read(value))));
  } else {
    apply(value);
  }
}

/** Fresh render: clone the template, bind, and append into `container`. */
export function render(result: TemplateResult, container: Node, scope: Scope = makeScope()): Scope {
  const compiled = compile(result.strings);
  const frag = compiled.template.content.cloneNode(true) as DocumentFragment;
  bindCompiled(frag, compiled, scope, result.values, false);
  container.appendChild(frag);
  return scope;
}

/**
 * Hydrate: adopt the existing server markup in `container`. Unlike a fresh
 * render, the server DOM has CONTENT between the `<!--hc:N-->`/`<!--/hc:N-->`
 * markers, which shifts sibling indices — so we cannot replay the empty-skeleton
 * paths. Instead we walk the live DOM to locate each marker by its hole index,
 * skipping the content range of each node hole so we only pick up THIS template's
 * markers (nested templates manage their own).
 */
function collectMarkers(parent: Node, found: Map<number, Node>): void {
  let n: Node | null = parent.firstChild;
  while (n) {
    if (n.nodeType === Node.COMMENT_NODE) {
      const m = /^hc:(\d+)$/.exec((n as Comment).data);
      if (m) {
        found.set(Number(m[1]), n);
        const endTag = `/hc:${m[1]}`;
        let e: Node | null = n.nextSibling;
        while (e && !(e.nodeType === Node.COMMENT_NODE && (e as Comment).data === endTag)) e = e.nextSibling;
        n = e ? e.nextSibling : null;
        continue;
      }
    } else if (n.nodeType === Node.ELEMENT_NODE) {
      const el = n as Element;
      for (const attr of el.attributes) {
        const m = /^data-hcb-(\d+)$/.exec(attr.name);
        if (m) found.set(Number(m[1]), el);
      }
      collectMarkers(el, found);
    }
    n = n.nextSibling;
  }
}

export function hydrate(result: TemplateResult, container: Node, scope: Scope = makeScope()): Scope {
  const compiled = compile(result.strings);
  const found = new Map<number, Node>();
  collectMarkers(container, found);
  for (const part of compiled.parts) {
    const node = found.get(part.index);
    if (node) bindPart(node, part, scope, result.values[part.index], true);
  }
  return scope;
}
